import { Component, signal, computed, OnInit, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import {
    TradingProcedureLandingApi,
    TradingProcedureLandingResponseDto,
    TradingProcedureStatusDto,
    TradingProcedureTypeDto,
} from '@tradgine/data-access/tradgine-data';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [CommonModule, DatePipe],
    template: `
        <div class="trading-procedures-list">
            @if (isLoading()) {
                <div class="loading" i18n="@@public-list.loading">Loading...</div>
            } @else if (error()) {
                <div class="error">{{ error() }}</div>
            } @else if (tradingProcedures().length === 0) {
                <div class="empty" i18n="@@public-list.empty">No trading procedures available</div>
            } @else {
                <div class="timeline">
                    @for (procedure of tradingProcedures(); track procedure.id) {
                        <div class="timeline-item">
                            <div class="timeline-left">
                                @if (isSealed(procedure)) {
                                    <span class="date-prefix" i18n="@@public-list.date-prefix.until">until</span>
                                }
                                <span class="date-value">{{ getRelevantDate(procedure) | date:'dd.MM HH:mm' }}</span>
                            </div>

                            <div class="timeline-logo">
                                @if (procedure.sellerOrganizationLogo) {
                                    <img class="logo-img" [src]="procedure.sellerOrganizationLogo" [alt]="procedure.sellerOrganizationName || ''" />
                                } @else {
                                    <div class="logo-placeholder">
                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                                        </svg>
                                    </div>
                                }
                            </div>

                            <div class="timeline-content">
                                <div class="procedure-title">{{ getFullTitle(procedure) }}</div>
                                @if (procedure.landingDescription) {
                                    <div class="procedure-description">{{ procedure.landingDescription }}</div>
                                }
                            </div>
                        </div>
                    }
                </div>
            }
        </div>
    `,
    styles: [],
})
export class AppComponent implements OnInit {
    private readonly api = inject(TradingProcedureLandingApi);
    private readonly allTradingProcedures = signal<TradingProcedureLandingResponseDto[]>([]);
    readonly isLoading = signal(true);
    readonly error = signal<string | null>(null);

    readonly tradingProcedures = computed(() => {
        const filtered = this.allTradingProcedures().filter(
            (p) => p.status !== TradingProcedureStatusDto.Draft
        );

        const sorted = filtered.sort((a, b) => {
            const dateA = this.getRelevantDateValue(a);
            const dateB = this.getRelevantDateValue(b);
            return dateB - dateA;
        });

        return sorted.slice(0, 15);
    });

    private getRelevantDateValue(procedure: TradingProcedureLandingResponseDto): number {
        const dateStr = procedure.type === TradingProcedureTypeDto.English
            ? (procedure.startDateTime || procedure.endDateTime)
            : procedure.endDateTime;
        return new Date(dateStr).getTime();
    }

    ngOnInit(): void {
        this.loadTradingProcedures();
    }

    private loadTradingProcedures(): void {
        this.api.getAll().subscribe((result) => {
            if (result.isOk()) {
                this.allTradingProcedures.set(result.unwrap());
            } else {
                this.error.set($localize`:@@public-list.error:Failed to load trading procedures`);
            }
            this.isLoading.set(false);
        });
    }

    getRelevantDate(procedure: TradingProcedureLandingResponseDto): string {
        return procedure.type === TradingProcedureTypeDto.English
            ? (procedure.startDateTime || procedure.endDateTime)
            : procedure.endDateTime;
    }

    getFullTitle(procedure: TradingProcedureLandingResponseDto): string {
        const typeLabel = this.getTypeLabel(procedure.type);
        const name = this.getDisplayName(procedure);
        return `${typeLabel} ${name}`;
    }

    private getDisplayName(procedure: TradingProcedureLandingResponseDto): string {
        if (procedure.showSellerName && procedure.sellerOrganizationName) {
            return procedure.sellerOrganizationName;
        }
        if (procedure.name) {
            return procedure.name;
        }
        return $localize`:@@public-list.unknown:Unknown`;
    }

    private getTypeLabel(type: TradingProcedureTypeDto): string {
        const labels: Record<TradingProcedureTypeDto, string> = {
            [TradingProcedureTypeDto.English]: $localize`:@@public-list.type.english:Ascending auction`,
            [TradingProcedureTypeDto.Sealed]: $localize`:@@public-list.type.sealed:Sealed-bid auction`,
        };
        return labels[type] || type;
    }

    isSealed(procedure: TradingProcedureLandingResponseDto): boolean {
        return procedure.type === TradingProcedureTypeDto.Sealed;
    }
}
